import React, { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { X } from "lucide-react";

/**
 * Fullscreen barcode scanner overlay. Uses back camera on mobile.
 * Props:
 *  - onDetected(code): called once when a code is decoded successfully
 *  - onClose(): close scanner
 */
export default function BarcodeScanner({ onDetected, onClose }) {
  const containerId = "rivaco-scanner-container";
  const scannerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: { exact: "environment" } },
          { fps: 12, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            if (firedRef.current) return;
            firedRef.current = true;
            try { if (navigator.vibrate) navigator.vibrate(120); } catch (_) {}
            onDetected?.(decoded);
          },
          () => {}
        ).catch(async () => {
          // Fallback if exact env camera not available
          await scanner.start(
            { facingMode: "environment" },
            { fps: 12, qrbox: { width: 260, height: 160 } },
            (decoded) => {
              if (firedRef.current) return;
              firedRef.current = true;
              try { if (navigator.vibrate) navigator.vibrate(120); } catch (_) {}
              onDetected?.(decoded);
            },
            () => {}
          );
        });
      } catch (e) {
        console.error("Scanner start failed", e);
        onClose?.();
      }
    };
    start();
    return () => {
      (async () => {
        try {
          if (scannerRef.current) {
            const s = scannerRef.current;
            const state = s.getState?.();
            if (state === 2) await s.stop();
            await s.clear();
          }
        } catch (_) {}
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black">
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-4 text-white bg-gradient-to-b from-black/70 to-transparent">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] opacity-70">RIVACO</div>
          <div className="font-serif-display text-xl">Barkod Tara</div>
        </div>
        <button
          data-testid="close-scanner"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div id={containerId} className="w-full h-full flex items-center justify-center" />
      <div className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-2 text-white/85 text-sm px-6 text-center">
        <div className="w-16 h-1 rounded-full bg-amber-400/70" />
        <div>Barkodu ekrandaki kutuya doğrultun</div>
      </div>
    </div>
  );
}
